/**
 * Maps Langfuse trace names onto the logical AI pipeline they belong to.
 *
 * WHY THIS EXISTS: in this codebase every pipeline STEP is its own Langfuse
 * trace — there is no parent trace spanning a whole document run (each stage
 * calls `runWithLangfuseRootTrace` independently, see
 * `backend/functions/models/LoanApplication.js` and
 * `controllers/classifyDocument.js`). So the `traceName` dimension already IS
 * the step dimension; this file just gives those flat names their pipeline,
 * their order, and whether they represent a first attempt, a retry, or a
 * fallback to another provider.
 *
 * The step list mirrors `backend/functions/ai-pipeline-eval/config/stages.js`,
 * extended with the retry/fallback variants that actually appear in traces.
 * Keep them in sync — there is no shared source between repos.
 *
 * Splitting primary / retry / fallback is the point: retry and fallback spend
 * is pure overhead, and it is invisible when every variant is collapsed into
 * one "feature" row.
 */

export const VARIANT = {
  PRIMARY: "primary",
  RETRY: "retry",
  FALLBACK: "fallback",
  VERIFICATION: "verification",
};

export const VARIANT_LABEL = {
  [VARIANT.PRIMARY]: "First attempt",
  [VARIANT.RETRY]: "Retry",
  [VARIANT.FALLBACK]: "Provider fallback",
  [VARIANT.VERIFICATION]: "Verification pass",
};

/**
 * Exact trace name -> {step, variant}. Exact matching first means a name like
 * "…-anthropic-toolloop-final-retry" cannot be mis-bucketed by a greedy
 * suffix rule.
 */
const TRACE_MAP = {
  // --- Step 1: document classification ---
  classifyDocumentV3: { step: "classification", variant: VARIANT.PRIMARY },
  "classifyDocumentV3-verification": {
    step: "classification",
    variant: VARIANT.VERIFICATION,
  },
  "classifyDocumentV3-gemini-repair": {
    step: "classification",
    variant: VARIANT.RETRY,
  },
  "classifyDocumentV3-anthropic-sonnet": {
    step: "classification",
    variant: VARIANT.FALLBACK,
  },
  "classifyDocumentV3-openai-gpt41": {
    step: "classification",
    variant: VARIANT.FALLBACK,
  },
  "classifyDocumentV3-openai-gpt54": {
    step: "classification",
    variant: VARIANT.FALLBACK,
  },

  // --- Step 2: pick the documents a task needs ---
  "LoanApplication-selectRelevantDocumentsForTask-llm": {
    step: "docSelection",
    variant: VARIANT.PRIMARY,
  },

  // --- Step 3: processing checklist, step 1 (the expensive one) ---
  "LoanApplication-step1PerformLoanTask-llm": {
    step: "pcStep1",
    variant: VARIANT.PRIMARY,
  },
  "LoanApplication-step1PerformLoanTask-toolloop": {
    step: "pcStep1",
    variant: VARIANT.PRIMARY,
  },
  "LoanApplication-step1PerformLoanTask-anthropic": {
    step: "pcStep1",
    variant: VARIANT.PRIMARY,
  },
  "LoanApplication-step1PerformLoanTask-anthropic-toolloop": {
    step: "pcStep1",
    variant: VARIANT.PRIMARY,
  },
  "LoanApplication-step1PerformLoanTask-llm-retry": {
    step: "pcStep1",
    variant: VARIANT.RETRY,
  },
  "LoanApplication-step1PerformLoanTask-toolloop-retry": {
    step: "pcStep1",
    variant: VARIANT.RETRY,
  },
  "LoanApplication-step1PerformLoanTask-toolloop-final-retry": {
    step: "pcStep1",
    variant: VARIANT.RETRY,
  },
  "LoanApplication-step1PerformLoanTask-anthropic-toolloop-retry": {
    step: "pcStep1",
    variant: VARIANT.RETRY,
  },
  "LoanApplication-step1PerformLoanTask-anthropic-toolloop-final-retry": {
    step: "pcStep1",
    variant: VARIANT.RETRY,
  },
  "LoanApplication-step1PerformLoanTask-luna-toolloop-final-retry": {
    step: "pcStep1",
    variant: VARIANT.RETRY,
  },
  "LoanApplication-step1PerformLoanTask-llm-luna-fallback": {
    step: "pcStep1",
    variant: VARIANT.FALLBACK,
  },
  "LoanApplication-step1PerformLoanTask-llm-openai-fallback": {
    step: "pcStep1",
    variant: VARIANT.FALLBACK,
  },

  // --- Step 4: render the checklist result ---
  "LoanApplication-step3GetTheUIRepresentation-llm": {
    step: "pcStep3",
    variant: VARIANT.PRIMARY,
  },

  // --- Step 5: summarize ---
  "LoanApplication-summarizeLoanProcessingChecklist-llm": {
    step: "pcSummary",
    variant: VARIANT.PRIMARY,
  },

  // --- Conditions & suggestions pipeline ---
  "LoanApplication-extractLoanApprovalConditionsFromConditionalApprovalDocumentTextContent-llm":
    { step: "extractConditions", variant: VARIANT.PRIMARY },
  "LoanApplication-deduplicateApprovalConditions-llm": {
    step: "dedupeConditions",
    variant: VARIANT.PRIMARY,
  },
  "LoanApplication-extractLoanSuggestionsFromFindingsTextContent-llm": {
    step: "extractSuggestions",
    variant: VARIANT.PRIMARY,
  },
  "LoanApplication-deduplicateLoanSuggestions-llm": {
    step: "dedupeSuggestions",
    variant: VARIANT.PRIMARY,
  },

  // --- Misc ---
  "LoanApplication-generateLoanApplicationTitle-llm": {
    step: "generateTitle",
    variant: VARIANT.PRIMARY,
  },
  "extend-ai-ocr": { step: "ocr", variant: VARIANT.PRIMARY },
};

export const STEPS = {
  classification: {
    id: "classification",
    label: "Document classification",
    pipeline: "docProcessing",
    order: 1,
  },
  ocr: { id: "ocr", label: "OCR / parse", pipeline: "docProcessing", order: 0 },
  docSelection: {
    id: "docSelection",
    label: "Select relevant documents",
    pipeline: "processingChecklist",
    order: 1,
  },
  pcStep1: {
    id: "pcStep1",
    label: "PC Step 1 — perform loan task",
    pipeline: "processingChecklist",
    order: 2,
  },
  pcStep3: {
    id: "pcStep3",
    label: "PC Step 3 — UI representation",
    pipeline: "processingChecklist",
    order: 3,
  },
  pcSummary: {
    id: "pcSummary",
    label: "PC summary",
    pipeline: "processingChecklist",
    order: 4,
  },
  extractConditions: {
    id: "extractConditions",
    label: "Extract approval conditions",
    pipeline: "conditions",
    order: 1,
  },
  dedupeConditions: {
    id: "dedupeConditions",
    label: "Deduplicate conditions",
    pipeline: "conditions",
    order: 2,
  },
  extractSuggestions: {
    id: "extractSuggestions",
    label: "Extract loan suggestions",
    pipeline: "conditions",
    order: 3,
  },
  dedupeSuggestions: {
    id: "dedupeSuggestions",
    label: "Deduplicate suggestions",
    pipeline: "conditions",
    order: 4,
  },
  generateTitle: {
    id: "generateTitle",
    label: "Generate loan title",
    pipeline: "other",
    order: 1,
  },
};

export const PIPELINES = {
  docProcessing: {
    id: "docProcessing",
    label: "Document processing",
    description:
      "Everything that runs when a document lands: OCR/parse, then classification (with its verification and provider fallbacks).",
    order: 1,
  },
  processingChecklist: {
    id: "processingChecklist",
    label: "Processing checklist (PC)",
    description:
      "The per-task checklist run: pick documents, perform the task, render it, summarize. PC Step 1 is historically the most expensive step in the product.",
    order: 2,
  },
  conditions: {
    id: "conditions",
    label: "Conditions & suggestions",
    description:
      "Extraction and de-duplication of approval conditions and loan suggestions from underwriting documents.",
    order: 3,
  },
  other: {
    id: "other",
    label: "Other",
    description: "Catalogued features that are not part of a multi-step pipeline.",
    order: 4,
  },
  uncatalogued: {
    id: "uncatalogued",
    label: "Uncatalogued",
    description:
      "Trace names with no entry in pipelines.js. These still cost money — add them to the catalog so they stop landing here.",
    order: 5,
  },
};

/**
 * Heuristic fallback for trace names not in TRACE_MAP. New variants get added
 * to the codebase faster than to this file, so an unmatched name must still
 * land somewhere visible with the right variant — never be dropped.
 */
const inferVariant = (traceName) => {
  const name = traceName.toLowerCase();
  if (name.includes("retry") || name.includes("repair")) return VARIANT.RETRY;
  if (name.includes("fallback")) return VARIANT.FALLBACK;
  if (name.includes("verification")) return VARIANT.VERIFICATION;
  return VARIANT.PRIMARY;
};

/**
 * Classify a trace name.
 * @returns {{stepId: string|null, pipelineId: string, variant: string, catalogued: boolean}}
 */
export const classifyTraceName = (traceName) => {
  if (!traceName) {
    return {
      stepId: null,
      pipelineId: "uncatalogued",
      variant: VARIANT.PRIMARY,
      catalogued: false,
    };
  }

  const exact = TRACE_MAP[traceName];
  if (exact) {
    return {
      stepId: exact.step,
      pipelineId: STEPS[exact.step]?.pipeline || "other",
      variant: exact.variant,
      catalogued: true,
    };
  }

  // Unknown name: try to attach it to a known step by prefix so a brand-new
  // "-llm-gemini-fallback" still rolls up under PC Step 1 rather than
  // disappearing into a generic bucket.
  const prefixMatch = Object.keys(TRACE_MAP)
    .filter((known) => traceName.startsWith(known))
    .sort((a, b) => b.length - a.length)[0];

  if (prefixMatch) {
    const step = TRACE_MAP[prefixMatch].step;
    return {
      stepId: step,
      pipelineId: STEPS[step]?.pipeline || "other",
      variant: inferVariant(traceName),
      catalogued: false,
    };
  }

  return {
    stepId: null,
    pipelineId: "uncatalogued",
    variant: inferVariant(traceName),
    catalogued: false,
  };
};

/**
 * Roll a list of per-traceName rows up into pipelines -> steps -> variants.
 *
 * @param {Array<{traceName: string, cost: number, runs: number, tokens: number}>} rows
 */
export const buildPipelineTree = (rows = []) => {
  const pipelines = new Map();

  rows.forEach((row) => {
    const { stepId, pipelineId, variant, catalogued } = classifyTraceName(
      row.traceName,
    );

    if (!pipelines.has(pipelineId)) {
      pipelines.set(pipelineId, {
        ...(PIPELINES[pipelineId] || PIPELINES.uncatalogued),
        cost: 0,
        runs: 0,
        tokens: 0,
        overheadCost: 0,
        steps: new Map(),
      });
    }
    const pipeline = pipelines.get(pipelineId);

    // Uncatalogued names have no step; key them on the trace name itself so
    // each stays individually visible instead of merging into one blob.
    const key = stepId || row.traceName;
    if (!pipeline.steps.has(key)) {
      pipeline.steps.set(key, {
        id: key,
        label: STEPS[stepId]?.label || row.traceName,
        order: STEPS[stepId]?.order ?? 99,
        cost: 0,
        runs: 0,
        tokens: 0,
        variants: [],
        catalogued,
      });
    }
    const step = pipeline.steps.get(key);

    step.cost += row.cost || 0;
    step.runs += row.runs || 0;
    step.tokens += row.tokens || 0;
    step.variants.push({ ...row, variant, catalogued });

    pipeline.cost += row.cost || 0;
    pipeline.runs += row.runs || 0;
    pipeline.tokens += row.tokens || 0;
    if (variant === VARIANT.RETRY || variant === VARIANT.FALLBACK) {
      pipeline.overheadCost += row.cost || 0;
    }
  });

  return Array.from(pipelines.values())
    .map((pipeline) => ({
      ...pipeline,
      steps: Array.from(pipeline.steps.values())
        .map((step) => ({
          ...step,
          variants: step.variants.sort((a, b) => (b.cost || 0) - (a.cost || 0)),
        }))
        .sort((a, b) => a.order - b.order || b.cost - a.cost),
    }))
    .sort((a, b) => b.cost - a.cost);
};
